<script setup>
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useToggle } from '@vueuse/core';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import WhatsAppCampaignEditDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignEditDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import ConfirmRetryFailedCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmRetryFailedCampaignDialog.vue';
import ConfirmStopCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmStopCampaignDialog.vue';
import CampaignDeliveryRecipientsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignDeliveryRecipientsDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import TeletelMetaBanner from 'dashboard/components-next/teletel/TeletelMetaBanner.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
// Owner order 2026-09-25: «إنشاء حملة» opens the three-step page.
const openNewCampaignPage = () =>
  router.push({
    name: 'campaigns_whatsapp_new',
    params: { accountId: route.params.accountId },
  });
const getters = useStoreGetters();
const store = useStore();

const selectedCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const confirmRetryFailedCampaignDialogRef = ref(null);
const confirmStopCampaignDialogRef = ref(null);
const deliveryRecipientsDialogRef = ref(null);

// Owner order 2026-09-14 (campaign audit, option 5): newest first, a search
// box and a status filter; the list refreshes itself while a campaign is
// sending, so the counters move without a manual reload.
const searchQuery = ref('');
const statusFilter = ref('all');
const statusOptions = ['all', 'scheduled', 'paused', 'processing', 'completed'];

const campaignStatusKey = campaign => {
  if (campaign.campaign_status === 'active')
    return campaign.enabled === false ? 'paused' : 'scheduled';
  return campaign.campaign_status;
};

const AllWhatsAppCampaigns = computed(() =>
  [...(getters['campaigns/getWhatsAppCampaigns'].value || [])].sort(
    (a, b) => b.id - a.id
  )
);

const WhatsAppCampaigns = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  return AllWhatsAppCampaigns.value.filter(campaign => {
    if (
      statusFilter.value !== 'all' &&
      campaignStatusKey(campaign) !== statusFilter.value
    )
      return false;
    if (!query) return true;
    return (
      (campaign.title || '').toLowerCase().includes(query) ||
      (campaign.message || '').toLowerCase().includes(query) ||
      (campaign.inbox?.name || '').toLowerCase().includes(query)
    );
  });
});

const hasNoWhatsAppCampaigns = computed(
  () => AllWhatsAppCampaigns.value.length === 0 && !isFetchingCampaigns.value
);

const hasProcessing = computed(() =>
  AllWhatsAppCampaigns.value.some(c => c.campaign_status === 'processing')
);
const pollTimerId = ref(null);
const stopPolling = () => {
  if (pollTimerId.value) window.clearInterval(pollTimerId.value);
  pollTimerId.value = null;
};
watch(
  hasProcessing,
  active => {
    stopPolling();
    if (active) {
      pollTimerId.value = window.setInterval(
        () => store.dispatch('campaigns/get'),
        15000
      );
    }
  },
  { immediate: true }
);

const editCampaignDialogRef = ref(null);
const handleEdit = async campaign => {
  selectedCampaign.value = campaign;
  await nextTick();
  editCampaignDialogRef.value?.dialogRef?.open();
};

const setEnabled = async (campaign, enabled) => {
  try {
    await store.dispatch('campaigns/update', { id: campaign.id, enabled });
    useAlert(
      t(
        enabled
          ? 'CAMPAIGN.WHATSAPP.PAUSE.RESUMED'
          : 'CAMPAIGN.WHATSAPP.PAUSE.PAUSED'
      )
    );
  } catch (error) {
    useAlert(error?.message || t('CAMPAIGN.WHATSAPP.EDIT.API.ERROR_MESSAGE'));
  }
};
const handlePause = campaign => setEnabled(campaign, false);
const handleResume = campaign => setEnabled(campaign, true);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleRetryFailed = campaign => {
  selectedCampaign.value = campaign;
  confirmRetryFailedCampaignDialogRef.value.dialogRef.open();
};

// Owner order 2026-10-06: resume a campaign the circuit breaker paused.
const handleResumeMeta = async campaign => {
  try {
    await store.dispatch('campaigns/resumeMeta', campaign.id);
    useAlert(t('CAMPAIGN.WHATSAPP.RESUME_META.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.RESUME_META.ERROR_MESSAGE')
    );
  }
};

const handleStop = campaign => {
  selectedCampaign.value = campaign;
  confirmStopCampaignDialogRef.value?.dialogRef?.open();
};

const handleViewRecipients = async campaign => {
  selectedCampaign.value = campaign;
  // The dialog fetches on open using its `selectedCampaign` prop; without
  // waiting for the prop to flush it would fetch the previously selected
  // campaign's recipients (release-audit finding C1).
  await nextTick();
  deliveryRecipientsDialogRef.value?.open();
};

// Syncing delivery history writes rows, so it must never run as a side effect
// of opening the page. It is an explicit, administrator-confirmed action.
const { isAdmin } = useAdmin();
const syncHistoryDialogRef = ref(null);
const isSyncingHistory = ref(false);
const refetchTimerId = ref(null);

const handleSyncHistoryConfirm = async () => {
  if (isSyncingHistory.value) return;
  isSyncingHistory.value = true;
  try {
    await store.dispatch('campaigns/syncDeliveryHistory');
    useAlert(t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.API.SUCCESS_MESSAGE'));
    syncHistoryDialogRef.value?.close();
    refetchTimerId.value = window.setTimeout(
      () => store.dispatch('campaigns/get'),
      1500
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.API.ERROR_MESSAGE')
    );
  } finally {
    isSyncingHistory.value = false;
  }
};

onMounted(() => {
  store.dispatch('campaigns/get');
});

onActivated(() => {
  store.dispatch('campaigns/get');
});

onBeforeUnmount(() => {
  if (refetchTimerId.value) window.clearTimeout(refetchTimerId.value);
  stopPolling();
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="openNewCampaignPage"
    @close="toggleWhatsAppCampaignDialog(false)"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        @close="toggleWhatsAppCampaignDialog(false)"
      />
    </template>
    <TeletelMetaBanner v-if="isAdmin" />
    <div
      v-if="isAdmin || !hasNoWhatsAppCampaigns"
      class="flex flex-wrap items-center gap-2 pb-3"
    >
      <template v-if="!hasNoWhatsAppCampaigns">
        <input
          v-model="searchQuery"
          type="search"
          class="mb-0 h-8 flex-1 min-w-40 text-sm"
          :placeholder="t('CAMPAIGN.WHATSAPP.FILTER.SEARCH')"
        />
        <select v-model="statusFilter" class="mb-0 h-8 w-auto text-sm">
          <option v-for="option in statusOptions" :key="option" :value="option">
            {{ t(`CAMPAIGN.WHATSAPP.FILTER.STATUS.${option.toUpperCase()}`) }}
          </option>
        </select>
      </template>
      <Button
        v-if="isAdmin"
        :label="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.BUTTON')"
        icon="i-lucide-refresh-cw"
        size="sm"
        color="slate"
        variant="faded"
        @click="syncHistoryDialogRef?.open()"
      />
    </div>
    <div
      v-if="isFetchingCampaigns && AllWhatsAppCampaigns.length === 0"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      is-whats-app-type
      @edit="handleEdit"
      @pause="handlePause"
      @resume="handleResume"
      @delete="handleDelete"
      @retry-failed="handleRetryFailed"
      @view-recipients="handleViewRecipients"
      @stop="handleStop"
      @resume-meta="handleResumeMeta"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <p
      v-if="!hasNoWhatsAppCampaigns && WhatsAppCampaigns.length === 0"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      {{ t('CAMPAIGN.WHATSAPP.FILTER.NO_MATCH') }}
    </p>
    <WhatsAppCampaignEditDialog
      ref="editCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmRetryFailedCampaignDialog
      ref="confirmRetryFailedCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmStopCampaignDialog
      ref="confirmStopCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignDeliveryRecipientsDialog
      ref="deliveryRecipientsDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <Dialog
      ref="syncHistoryDialogRef"
      type="alert"
      :title="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.TITLE')"
      :description="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.DESCRIPTION')"
      :confirm-button-label="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.CONFIRM')"
      :is-loading="isSyncingHistory"
      :disable-confirm-button="isSyncingHistory"
      @confirm="handleSyncHistoryConfirm"
    />
  </CampaignLayout>
</template>
